/**
 * SES-011's story, in Calder & Wynn (fixture-calder.ts): what needs a
 * decision before Friday. The firm keeps its decisions in a Decisions data
 * set in the Operations channel; the Twin reads the ones due by Friday that
 * are still open, then the evidence behind each in Outlook, Xero and Google
 * Drive. Three different decisions (a delivery date, a supplier's query, a
 * renewal term), each with its recorded owner; nothing is sent or changed.
 * `fixture-decisions.test.ts` checks the answer against the records and
 * every printed value against the application's formatters.
 */

import type { WorkspaceField } from "@product/lib/mlx-workspace-client";

import {
  CALDER_CHANNELS,
  CALDER_TURN_TIMES,
  calderRecordHref,
  calderWorkspace,
  sessionsRunning,
  workflowField,
} from "./fixture-calder";

export const DECISIONS_CHANNEL = CALDER_CHANNELS.operations;
export const DECISIONS_DATA_SET_NAME = "Decisions";

export const DECISIONS_WORKSPACE = calderWorkspace({
  sessionId: "ses_decisions_friday",
  sessionTitle: "Decisions due by Friday",
});

/** The rail while the run works. */
export const DECISIONS_SESSIONS_RUNNING = sessionsRunning(DECISIONS_WORKSPACE);

export const DECISIONS_TURN_TIMES = CALDER_TURN_TIMES;

/** The question's Friday. */
export const DECISIONS_DUE = "2026-09-25";

/* ------------------------------------------------------------------ */
/*  The decisions                                                       */
/* ------------------------------------------------------------------ */

export type DecisionRecord = {
  id: string;
  /** What the decision is about: the record's title. */
  name: string;
  decision: string;
  counterparty: string;
  due: string;
  owner: string;
  plannedDate: string | null;
  requestedDate: string | null;
  /** The figure the evidence quotes, where there is one. */
  amount: number | null;
  evidence: string;
  nextStep: string;
};

export const DECISIONS: readonly DecisionRecord[] = [
  {
    id: "rec_dec_northgate",
    name: "Northgate relaunch",
    decision: "Confirm the delivery date",
    counterparty: "Kestrel Freight",
    due: DECISIONS_DUE,
    owner: "Ana Duarte",
    plannedDate: "2026-10-09",
    requestedDate: "2026-10-16",
    amount: null,
    evidence: "The plan has delivery on 9 October. Kestrel Freight asked on 21 September to move it to the 16th.",
    nextStep: "Agree 16 October with Kestrel, or offer a partial delivery on the 9th.",
  },
  {
    id: "rec_dec_inv_8812",
    name: "INV-8812 · Lumen Print",
    decision: "Resolve the supplier's query",
    counterparty: "Lumen Print",
    due: DECISIONS_DUE,
    owner: "Leah Carter",
    plannedDate: null,
    requestedDate: null,
    amount: 6_480,
    evidence: "The bill falls due on 30 September. Lumen Print asked on 22 September to split it across two purchase orders.",
    nextStep: "Confirm the purchase-order split so the bill can be paid on time.",
  },
  {
    id: "rec_dec_cleaning",
    name: "Cleaning contract",
    decision: "Choose a renewal term",
    counterparty: "Ashgrove Facilities",
    due: DECISIONS_DUE,
    owner: "Tom Reeve",
    plannedDate: null,
    requestedDate: null,
    amount: 14_400,
    evidence: "The contract renews on 1 October. Ashgrove's quote offers 12 months at £14,400 or 24 months at £12,960 a year.",
    nextStep: "Choose 12 or 24 months and reply to Ashgrove.",
  },
];

export const DECISION_STATUS = {
  open: { value: "open", label: "Open", color: "amber" },
  decided: { value: "decided", label: "Decided", color: "green" },
} as const;

/** The Decisions data set's schema, in the order the sheet lists it. */
export const DECISION_FIELDS: WorkspaceField[] = [
  workflowField("name", "Name", "text", { primary: true }),
  workflowField("decision", "Decision", "text"),
  workflowField("status", "Status", "select", { options: [DECISION_STATUS.open, DECISION_STATUS.decided] }),
  workflowField("due", "Decision due", "date"),
  workflowField("owner", "Owner", "text"),
  workflowField("counterparty", "Client or supplier", "text"),
  workflowField("planned_date", "Planned date", "date"),
  workflowField("requested_date", "Requested date", "date"),
  workflowField("evidence", "Evidence", "text", { display: "multiline" }),
  workflowField("next_step", "Next step", "text", { display: "multiline" }),
];

/** A decision's values, keyed by field. */
export function decisionValues(record: DecisionRecord): Record<string, unknown> {
  return {
    name: record.name,
    decision: record.decision,
    status: DECISION_STATUS.open.value,
    due: record.due,
    owner: record.owner,
    counterparty: record.counterparty,
    planned_date: record.plannedDate,
    requested_date: record.requestedDate,
    evidence: record.evidence,
    next_step: record.nextStep,
  };
}

/**
 * Each field as the record sheet prints it (formatFieldValue's text), written
 * out because the formatter reads @mlx/contracts at runtime; the test checks
 * every one against the application's formatter.
 */
export const DECISION_DISPLAYS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  rec_dec_northgate: {
    name: "Northgate relaunch",
    decision: "Confirm the delivery date",
    due: "25 Sep 2026",
    owner: "Ana Duarte",
    counterparty: "Kestrel Freight",
    planned_date: "9 Oct 2026",
    requested_date: "16 Oct 2026",
    evidence: "The plan has delivery on 9 October. Kestrel Freight asked on 21 September to move it to the 16th.",
    next_step: "Agree 16 October with Kestrel, or offer a partial delivery on the 9th.",
  },
  rec_dec_inv_8812: {
    name: "INV-8812 · Lumen Print",
    decision: "Resolve the supplier's query",
    due: "25 Sep 2026",
    owner: "Leah Carter",
    counterparty: "Lumen Print",
    planned_date: "-",
    requested_date: "-",
    evidence: "The bill falls due on 30 September. Lumen Print asked on 22 September to split it across two purchase orders.",
    next_step: "Confirm the purchase-order split so the bill can be paid on time.",
  },
  rec_dec_cleaning: {
    name: "Cleaning contract",
    decision: "Choose a renewal term",
    due: "25 Sep 2026",
    owner: "Tom Reeve",
    counterparty: "Ashgrove Facilities",
    planned_date: "-",
    requested_date: "-",
    evidence: "The contract renews on 1 October. Ashgrove's quote offers 12 months at £14,400 or 24 months at £12,960 a year.",
    next_step: "Choose 12 or 24 months and reply to Ashgrove.",
  },
};

export function decisionHref(record: Pick<DecisionRecord, "id">): string {
  return calderRecordHref(DECISIONS_CHANNEL.id, record.id);
}

/* ------------------------------------------------------------------ */
/*  The turn                                                            */
/* ------------------------------------------------------------------ */

export const DECISIONS_QUESTION = "What needs a decision before Friday?";

const [NORTHGATE, INVOICE, CLEANING] = DECISIONS as [DecisionRecord, DecisionRecord, DecisionRecord];

/** The records the answer links, in reading order: the sheet's previous and next. */
export const DECISIONS_ANSWER_RECORD_IDS = [NORTHGATE.id, INVOICE.id, CLEANING.id] as const;

/**
 * The answer as the run wrote it: what each decision is and why it is due,
 * then the evidence and owner side by side. Short enough that the question,
 * the answer and its sources fit one frame.
 */
export const DECISIONS_ANSWER_MARKDOWN = `Three decisions are due by Friday. [Northgate relaunch](${decisionHref(NORTHGATE)}) needs a delivery date: the plan has 9 October, but Kestrel Freight asked on Monday to move it to the 16th. Lumen Print's bill [INV-8812](${decisionHref(INVOICE)}) for **£6,480** falls due on 30 September, and the supplier has asked to split it across two purchase orders. The [cleaning contract](${decisionHref(CLEANING)}) renews on 1 October, and Ashgrove Facilities has quoted 12 or 24 months.

| Decision | Evidence | Owner |
| --- | --- | --- |
| Northgate delivery date | Plan: 9 Oct · Kestrel email, 21 Sept: 16 Oct | Ana Duarte |
| INV-8812 supplier query | Xero bill due 30 Sept · Lumen email, 22 Sept | Leah Carter |
| Cleaning contract term | Renews 1 Oct · Ashgrove quote, Google Drive | Tom Reeve |`;
